import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Users, Plus, Search, Phone, Mail, MapPin, Tag, 
  MoreVertical, X, Check, AlertCircle, RefreshCw,
  Briefcase, DollarSign, Filter, Download, Key, Lock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Clients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showLostModal, setShowLostModal] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [crmBlocked, setCrmBlocked] = useState(false);

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const fetchClients = async () => {
    try {
      let url = `${API}/clients`;
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('lead_status', statusFilter);
      }
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      const response = await axios.get(url);
      setClients(response.data);
      setCrmBlocked(false);
    } catch (error) {
      if (error.response?.status === 403) {
        setCrmBlocked(true);
      } else {
        toast.error('Failed to load clients');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleImportFromJobs = async () => {
    try {
      const response = await axios.post(`${API}/clients/import-from-jobs`);
      toast.success(response.data.message);
      fetchClients();
    } catch (error) {
      toast.error('Failed to import clients');
    }
  };

  const handleMarkLost = async (clientId, reason) => {
    try {
      await axios.post(`${API}/clients/${clientId}/mark-lost`, { reason });
      toast.success('Client marked as lost');
      setShowLostModal(null);
      fetchClients();
    } catch (error) {
      toast.error('Failed to update client');
    }
  };

  const handleReactivate = async (clientId) => {
    try {
      await axios.post(`${API}/clients/${clientId}/reactivate`);
      toast.success('Client reactivated');
      fetchClients();
    } catch (error) {
      toast.error('Failed to reactivate client');
    }
  };

  const filteredClients = clients.filter(client => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      client.name?.toLowerCase().includes(searchLower) ||
      client.email?.toLowerCase().includes(searchLower) ||
      client.phone?.includes(search) ||
      client.full_address?.toLowerCase().includes(searchLower)
    );
  });

  const statusColors = {
    active: 'bg-emerald-100 text-emerald-700',
    won: 'bg-blue-100 text-blue-700',
    lost: 'bg-red-100 text-red-700'
  };

  // Show upgrade message if CRM is blocked
  if (crmBlocked) {
    return (
      <div className="p-4 animate-fade-in" data-testid="clients-page-blocked">
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-6">
            <Lock className="w-10 h-10 text-slate-400" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
            Client Management
          </h1>
          <p className="text-slate-500 max-w-md mb-6">
            Upgrade your plan to access the Client Management (CRM) feature and keep track of all your customers in one place.
          </p>
          <Button
            onClick={() => navigate('/profile')}
            className="bg-[#FF5F00] hover:bg-orange-600 text-white"
          >
            Upgrade Plan
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 animate-fade-in" data-testid="clients-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-dark">Clients</h1>
          <p className="text-slate-500">Manage your customer database</p>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          data-testid="add-client-btn"
          className="bg-[#FF5F00] hover:bg-orange-600 text-white"
        >
          <Plus className="w-5 h-5 mr-1" />
          Add
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search clients..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="client-search"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 rounded-lg border-2 border-slate-200 bg-white text-sm font-medium"
            data-testid="status-filter"
          >
            <option value="all">All Leads</option>
            <option value="active">Active</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
          </select>
          <Button
            variant="outline"
            onClick={handleImportFromJobs}
            className="whitespace-nowrap"
            data-testid="import-clients-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            Import
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-white rounded-xl border-2 border-slate-200 p-3 text-center">
          <p className="text-2xl font-bold text-slate-dark">{clients.filter(c => c.lead_status === 'active').length}</p>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Active</p>
        </div>
        <div className="bg-white rounded-xl border-2 border-slate-200 p-3 text-center">
          <p className="text-2xl font-bold text-emerald-600">{clients.filter(c => c.lead_status === 'won').length}</p>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Won</p>
        </div>
        <div className="bg-white rounded-xl border-2 border-slate-200 p-3 text-center">
          <p className="text-2xl font-bold text-red-500">{clients.filter(c => c.lead_status === 'lost').length}</p>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Lost</p>
        </div>
      </div>

      {/* Client List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-slate-200 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : filteredClients.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-slate-200">
          <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-bold text-slate-dark mb-2">No Clients Yet</h3>
          <p className="text-slate-500 mb-4">Add clients manually or import from your job history</p>
          <div className="flex justify-center gap-3">
            <Button onClick={() => setShowAddModal(true)} className="bg-[#FF5F00] hover:bg-orange-600">
              <Plus className="w-4 h-4 mr-1" /> Add Client
            </Button>
            <Button variant="outline" onClick={handleImportFromJobs}>
              <Download className="w-4 h-4 mr-1" /> Import from Jobs
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredClients.map((client) => (
            <div
              key={client.id}
              data-testid={`client-card-${client.id}`}
              className="bg-white rounded-xl border-2 border-slate-200 shadow-hard overflow-hidden"
            >
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-heading text-lg font-bold text-slate-dark truncate">
                        {client.name}
                      </h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${statusColors[client.lead_status]}`}>
                        {client.lead_status}
                      </span>
                    </div>
                    {client.lead_status === 'lost' && client.lost_reason && (
                      <p className="text-red-500 text-sm mb-2">
                        <AlertCircle className="w-4 h-4 inline mr-1" />
                        {client.lost_reason}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {client.lead_status === 'lost' ? (
                      <button
                        onClick={() => handleReactivate(client.id)}
                        className="p-2 rounded-lg hover:bg-slate-100 text-emerald-600"
                        title="Reactivate"
                      >
                        <RefreshCw className="w-5 h-5" />
                      </button>
                    ) : (
                      <button
                        onClick={() => setShowLostModal(client)}
                        className="p-2 rounded-lg hover:bg-slate-100 text-red-500"
                        title="Mark as Lost"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-2 text-sm text-slate-600 mb-3">
                  {client.phone && (
                    <a href={`tel:${client.phone}`} className="flex items-center gap-1 hover:text-[#FF5F00]">
                      <Phone className="w-4 h-4" /> {client.phone}
                    </a>
                  )}
                  {client.email && (
                    <a href={`mailto:${client.email}`} className="flex items-center gap-1 hover:text-[#FF5F00] truncate">
                      <Mail className="w-4 h-4" /> {client.email}
                    </a>
                  )}
                </div>

                {client.full_address && (
                  <p className="flex items-center gap-1 text-sm text-slate-500 mb-3">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{client.full_address}</span>
                  </p>
                )}

                {/* Tags */}
                {client.tags && client.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {client.tags.map((tag, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-slate-100 rounded-full text-xs text-slate-600">
                        <Tag className="w-3 h-3 inline mr-1" />{tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Access Details */}
                {(client.gate_code || client.lockbox_code || client.access_notes) && (
                  <div className="flex flex-wrap gap-2 mb-3 p-2 bg-amber-50 rounded-lg border border-amber-200">
                    <Key className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-amber-800">
                      {client.gate_code && <span><strong>Gate:</strong> {client.gate_code}</span>}
                      {client.lockbox_code && <span><strong>Lockbox:</strong> {client.lockbox_code}</span>}
                      {client.access_notes && <span className="w-full"><strong>Access:</strong> {client.access_notes}</span>}
                    </div>
                  </div>
                )}

                {/* Stats */}
                <div className="flex items-center gap-4 pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-1 text-slate-600">
                    <Briefcase className="w-4 h-4" />
                    <span className="text-sm font-medium">{client.total_jobs} jobs</span>
                  </div>
                  <div className="flex items-center gap-1 text-emerald-600">
                    <DollarSign className="w-4 h-4" />
                    <span className="text-sm font-medium">${(client.total_revenue || 0).toLocaleString()}</span>
                  </div>
                  <button
                    onClick={() => navigate(`/client/${client.id}`)}
                    className="ml-auto text-sm text-[#FF5F00] font-medium hover:underline"
                  >
                    View Details →
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Client Modal */}
      {showAddModal && (
        <AddClientModal
          onClose={() => setShowAddModal(false)}
          onSave={() => {
            setShowAddModal(false);
            fetchClients();
          }}
        />
      )}

      {/* Mark Lost Modal */}
      {showLostModal && (
        <MarkLostModal
          client={showLostModal}
          onClose={() => setShowLostModal(null)}
          onConfirm={handleMarkLost}
        />
      )}
    </div>
  );
}

// Add Client Modal Component
function AddClientModal({ onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address_street: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    notes: '',
    tags: [],
    gate_code: '',
    lockbox_code: '',
    access_notes: ''
  });
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/clients`, formData);
      toast.success('Client added');
      onSave();
    } catch (error) {
      toast.error('Failed to add client');
    } finally {
      setSaving(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-heading text-xl font-bold">Add Client</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 block">Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="John Smith"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 block">Phone</label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="555-123-4567"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 block">Email</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 block">Street Address</label>
            <Input
              value={formData.address_street}
              onChange={(e) => setFormData({ ...formData, address_street: e.target.value })}
              placeholder="123 Main St"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 block">City</label>
              <Input
                value={formData.address_city}
                onChange={(e) => setFormData({ ...formData, address_city: e.target.value })}
                placeholder="Austin"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 block">State</label>
              <Input
                value={formData.address_state}
                onChange={(e) => setFormData({ ...formData, address_state: e.target.value })}
                placeholder="TX"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 block">Zip</label>
              <Input
                value={formData.address_zip}
                onChange={(e) => setFormData({ ...formData, address_zip: e.target.value })}
                placeholder="78701"
              />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 block">Tags</label>
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="Add tag..."
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              />
              <Button type="button" variant="outline" onClick={addTag}>Add</Button>
            </div>
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {formData.tags.map((tag, idx) => (
                  <span key={idx} className="px-2 py-1 bg-slate-100 rounded-full text-sm flex items-center gap-1">
                    {tag}
                    <button type="button" onClick={() => removeTag(tag)} className="hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          
          {/* Access Details Section */}
          <div className="border-t border-slate-200 pt-4 mt-2">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">Access Details</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 block">Gate Code</label>
                <Input
                  value={formData.gate_code}
                  onChange={(e) => setFormData({ ...formData, gate_code: e.target.value })}
                  placeholder="#1234"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 block">Lockbox Code</label>
                <Input
                  value={formData.lockbox_code}
                  onChange={(e) => setFormData({ ...formData, lockbox_code: e.target.value })}
                  placeholder="5678"
                />
              </div>
            </div>
            <div className="mt-3">
              <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 block">Access Notes</label>
              <Input
                value={formData.access_notes}
                onChange={(e) => setFormData({ ...formData, access_notes: e.target.value })}
                placeholder="e.g., Side gate, ring doorbell twice..."
              />
            </div>
          </div>
          
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 block">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes..."
              className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg resize-none h-20"
            />
          </div>
          <Button
            type="submit"
            disabled={saving}
            className="w-full bg-[#FF5F00] hover:bg-orange-600 text-white h-12"
          >
            {saving ? 'Saving...' : 'Add Client'}
          </Button>
        </form>
      </div>
    </div>
  );
}

// Mark Lost Modal Component
function MarkLostModal({ client, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');

  const reasons = [
    'Chose competitor',
    'Price too high',
    'No longer needs service',
    'Unresponsive',
    'Project cancelled',
    'Other'
  ];

  const handleConfirm = () => {
    const finalReason = reason === 'Other' ? customReason : reason;
    if (!finalReason) {
      toast.error('Please select or enter a reason');
      return;
    }
    onConfirm(client.id, finalReason);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-heading text-xl font-bold text-red-600">Mark as Lost</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4">
          <p className="text-slate-600 mb-4">
            Why did you lose <strong>{client.name}</strong>?
          </p>
          <div className="space-y-2 mb-4">
            {reasons.map((r) => (
              <button
                key={r}
                onClick={() => setReason(r)}
                className={`w-full p-3 text-left rounded-lg border-2 transition-colors ${
                  reason === r
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                {r}
              </button>
            ))}
          </div>
          {reason === 'Other' && (
            <Input
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              placeholder="Enter reason..."
              className="mb-4"
            />
          )}
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              Mark as Lost
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
